import { GlobalEventService } from '@host/services/event.service';
import { useWorkplaceStore } from '@host/stores/workplace.store';
import { GlobalSocketService } from '@host/services/socket.service';
import { UserStatusChangedEvent, UserStatus } from 'nexgen-types';
import { useMultiFloorStateStore } from '@apps/office/stores/MultiFloorStore';
import { Logger } from '../../../shared/services/logger.service';

export class WorkplaceEventRouter {
  private static eventHandles: string[] = [];
  // private static isRegistered: boolean = false;

  /**
   * Register workplace event listeners
   */
  static registerEventListeners(): void {
    // Prevent duplicate registration
    // if (this.isRegistered) {
    //   Logger.debug('[WorkplaceEventRouter] ⚠️ Event listeners already registered, skipping...');
    //   return;
    // }
    // this.cleanup(); // Cleanup any previous registrations
    // Logger.debug('[WorkplaceEventRouter] 📡 Registering event listeners...');
    // // Subscribe to topic-based events (events that come from other users)
    // this.subscribeToPresenceEvents();
    // this.subscribeToRoomChangeEvents();
    // this.subscribeToStatusChangeEvents();
    // // 🔧 ADD MISSING EVENT HANDLERS
    // this.subscribeToMeetingEvents();
    // this.subscribeToUserEvents();
    // // this.isRegistered = true;
    // Logger.debug('[WorkplaceEventRouter] ✅ All event listeners registered successfully');
  }

  /**
   * Subscribe to presence change events from topics
   */
  private static subscribeToPresenceEvents(): void {
    // Listen for presence change topic events
    const handle1 = GlobalEventService.on('vo.user.presence.changed.topic', (event: any) => {
      Logger.debug('[WorkplaceEventRouter] 📨 Received presence update from topic:', event);
      this.handlePresenceUpdate(event);
    });

    // Only push handle if it's defined
    if (handle1 !== undefined) {
      this.eventHandles.push(handle1);
    }

    Logger.debug('[WorkplaceEventRouter] ✅ Subscribed to presence change topics');
  }

  /**
   * Subscribe to room change events from topics
   */
  private static subscribeToRoomChangeEvents(): void {
    // Listen for room change topic events
    const handle1 = GlobalEventService.on('vo.user.room.changed.topic', (event: any) => {
      Logger.debug('[WorkplaceEventRouter] 📨 Received room change from topic:', event);
      this.handleRoomChange(event);
    });

    // Only push handle if it's defined
    if (handle1 !== undefined) {
      this.eventHandles.push(handle1);
    }

    Logger.debug('[WorkplaceEventRouter] ✅ Subscribed to room change topics');
  }

  /**
   * Subscribe to status change events from topics
   */
  private static subscribeToStatusChangeEvents(): void {
    // Listen for status change topic events
    const handle1 = GlobalEventService.on('vo.user.status.changed.topic', (event: any) => {
      Logger.debug('[WorkplaceEventRouter] 📨 Received status change from topic:', event);
      this.handleStatusChange(event);
    });

    // Only push handle if it's defined
    if (handle1 !== undefined) {
      this.eventHandles.push(handle1);
    }

    Logger.debug('[WorkplaceEventRouter] ✅ Subscribed to status change topics');
  }

  /**
   * Subscribe to meeting events from topics
   */
  private static subscribeToMeetingEvents(): void {
    // Listen for meeting join topic events
    const handle1 = GlobalEventService.on('vo.meeting.join.topic', (event: any) => {
      Logger.debug('[WorkplaceEventRouter] 📨 Received meeting join from topic:', event);
      this.handleMeetingJoin(event);
    });

    // Listen for meeting leave topic events
    const handle2 = GlobalEventService.on('vo.meeting.leave.topic', (event: any) => {
      Logger.debug('[WorkplaceEventRouter] 📨 Received meeting leave from topic:', event);
      this.handleMeetingLeave(event);
    });

    // Only push handles if they're defined
    if (handle1 !== undefined) {
      this.eventHandles.push(handle1);
    }
    if (handle2 !== undefined) {
      this.eventHandles.push(handle2);
    }

    Logger.debug('[WorkplaceEventRouter] ✅ Subscribed to meeting events');
  }

  /**
   * Subscribe to user events from topics
   */
  private static subscribeToUserEvents(): void {
    // Listen for user login topic events
    const handle1 = GlobalEventService.on('vo.user.login.topic', (event: any) => {
      Logger.debug('[WorkplaceEventRouter] 📨 Received user login from topic:', event);
      this.handleUserLogin(event);
    });

    // Listen for user logout topic events
    const handle2 = GlobalEventService.on('vo.user.logout.topic', (event: any) => {
      Logger.debug('[WorkplaceEventRouter] 📨 Received user logout from topic:', event);
      this.handleUserLogout(event);
    });

    // Only push handles if they're defined
    if (handle1 !== undefined) {
      this.eventHandles.push(handle1);
    }
    if (handle2 !== undefined) {
      this.eventHandles.push(handle2);
    }

    Logger.debug('[WorkplaceEventRouter] ✅ Subscribed to user events');
  }

  /**
   * Handle incoming presence updates from other users
   */
  private static handlePresenceUpdate(event: any): void {
    try {
      // The event object IS the payload for topic events
      const payload = event;
      if (!payload || !payload.user_pid) {
        console.warn('[WorkplaceEventRouter] Invalid presence update event:', event);
        return;
      }

      Logger.debug('[WorkplaceEventRouter] 🔄 Processing presence update:', payload);

      // Skip if this is our own event
      const currentUserPid = useWorkplaceStore.getState().user.pid;
      if (payload.user_pid === currentUserPid) {
        Logger.debug('[WorkplaceEventRouter] Skipping own presence update');
        return;
      }

      // Update the UI with the presence change
      Logger.debug('[WorkplaceEventRouter] 📍 Other user presence update:', {
        user_pid: payload.user_pid,
        status: payload.status,
        room_pid: payload.room_pid,
        floor_pid: payload.floor_pid,
        message: payload.message,
      });

      // Update the workplace store with the presence change
      const workplaceStore = useWorkplaceStore.getState();
      if (workplaceStore.updateUserLocation) {
        workplaceStore.updateUserLocation({
          user_pid: payload.user_pid,
          room_pid: payload.room_pid,
          floor_pid: payload.floor_pid,
        });
        Logger.debug('[WorkplaceEventRouter] ✅ Updated user presence in workplace store');
      }

      Logger.debug('[WorkplaceEventRouter] ✅ Presence update processed successfully');
    } catch (error) {
      console.error('[WorkplaceEventRouter] ❌ Error handling presence update:', error);
    }
  }

  /**
   * Handle incoming room changes from other users
   */
  private static handleRoomChange(event: any): void {
    try {
      // The event object IS the payload for topic events
      const payload = event;
      if (!payload || !payload.user_pid) {
        console.warn('[WorkplaceEventRouter] Invalid room change event:', event);
        return;
      }

      Logger.debug('[WorkplaceEventRouter] 🔄 Processing room change:', payload);

      // Skip if this is our own event
      const currentUserPid = useWorkplaceStore.getState().user.pid;
      if (payload.user_pid === currentUserPid) {
        Logger.debug('[WorkplaceEventRouter] Skipping own room change');
        return;
      }

      // Update the UI with the room change
      Logger.debug('[WorkplaceEventRouter] 🏠 Other user room change:', {
        user_pid: payload.user_pid,
        room_pid: payload.room_pid,
        floor_pid: payload.floor_pid,
        previous_room_pid: payload.previous_room_pid,
      });

      // Update the workplace store with the new user location
      const workplaceStore = useWorkplaceStore.getState();
      if (workplaceStore.updateUserLocation) {
        workplaceStore.updateUserLocation({
          user_pid: payload.user_pid,
          room_pid: payload.room_pid,
          floor_pid: payload.floor_pid,
        });
        Logger.debug('[WorkplaceEventRouter] ✅ Updated user location in workplace store');
      }

      // 🔧 CRITICAL FIX: Also update the MultiFloorStore for real-time UI updates
      if (payload.floor_pid && payload.room_pid) {
        const multiFloorStore = useMultiFloorStateStore.getState();
        multiFloorStore.updateUserPresence(payload.floor_pid, payload.room_pid, payload.user_pid);
        Logger.debug('[WorkplaceEventRouter] ✅ Updated user presence in MultiFloorStore:', {
          floor_pid: payload.floor_pid,
          room_pid: payload.room_pid,
          user_pid: payload.user_pid,
        });
      }

      Logger.debug('[WorkplaceEventRouter] ✅ Room change processed successfully');
    } catch (error) {
      console.error('[WorkplaceEventRouter] ❌ Error handling room change:', error);
    }
  }

  /**
   * Handle incoming status changes from other users
   */
  private static handleStatusChange(event: any): void {
    try {
      // The event object IS the payload for topic events
      const payload = event;
      if (!payload || !payload.user_pid) {
        console.warn('[WorkplaceEventRouter] Invalid status change event:', event);
        return;
      }

      Logger.debug('[WorkplaceEventRouter] 🔄 Processing status change:', payload);

      // Skip if this is our own event
      const currentUserPid = useWorkplaceStore.getState().user.pid;
      if (payload.user_pid === currentUserPid) {
        Logger.debug('[WorkplaceEventRouter] Skipping own status change');
        return;
      }

      // Update the UI with the status change
      Logger.debug('[WorkplaceEventRouter] 📊 Other user status change:', {
        user_pid: payload.user_pid,
        status: payload.status,
        room_pid: payload.room_pid,
        floor_pid: payload.floor_pid,
        message: payload.message,
      });

      // Update the workplace store with the status change
      const workplaceStore = useWorkplaceStore.getState();
      if (workplaceStore.updateUserLocation) {
        workplaceStore.updateUserLocation({
          user_pid: payload.user_pid,
          room_pid: payload.room_pid,
          floor_pid: payload.floor_pid,
        });
        Logger.debug('[WorkplaceEventRouter] ✅ Updated user location in workplace store');
      }

      // 🔧 CRITICAL FIX: Update user status in MultiFloorStore for visual indicators
      if (payload.floor_pid) {
        const multiFloorStore = useMultiFloorStateStore.getState();
        multiFloorStore.updateUserStatus(
          payload.floor_pid,
          payload.user_pid,
          payload.status,
          payload.message
        );
        Logger.debug('[WorkplaceEventRouter] ✅ Updated user status in MultiFloorStore:', {
          floor_pid: payload.floor_pid,
          user_pid: payload.user_pid,
          status: payload.status,
          message: payload.message,
        });
      }

      Logger.debug('[WorkplaceEventRouter] ✅ Status change processed successfully');
    } catch (error) {
      console.error('[WorkplaceEventRouter] ❌ Error handling status change:', error);
    }
  }

  /**
   * 📊 Emit User Status Changed Event
   *
   * This emits a local event. The socket layer will automatically
   * route this to UserStatusChangedTopic for other users.
   */
  static emitUserStatusChanged(payload: {
    user_pid: string;
    status: UserStatus;
    org_pid?: string;
    floor_pid?: string;
    room_pid?: string;
    message?: string;
  }): void {
    try {
      Logger.debug('[WorkplaceEventRouter] Emitting UserStatusChanged:', payload);

      // 🔧 DEBUG: Clear no-handler cache for this event type
      const noHandlerTypes = GlobalSocketService.getNoHandlerEventTypes();
      if (noHandlerTypes.includes('vo.user.status.changed')) {
        console.warn(
          '[WorkplaceEventRouter] 🔧 vo.user.status.changed was in no-handler cache - clearing...'
        );
        GlobalSocketService.removeFromNoHandlerCache('vo.user.status.changed');
        Logger.debug(
          '[WorkplaceEventRouter] ✅ Cleared vo.user.status.changed from no-handler cache'
        );
      }

      // Create and emit typed event
      const event = UserStatusChangedEvent.create(payload);
      GlobalEventService.emitTyped(event, true);

      Logger.debug('[WorkplaceEventRouter] ✅ UserStatusChanged event emitted');
    } catch (error) {
      console.error('[WorkplaceEventRouter] ❌ Failed to emit UserStatusChanged:', error);
    }
  }

  /**
   * 🧹 Cleanup - Remove event listeners
   */
  static cleanup(): void {
    Logger.debug('[WorkplaceEventRouter] 🧹 Cleaning up event listeners...');

    // Remove all registered event handlers
    this.eventHandles.forEach((handle) => {
      GlobalEventService.remove(handle);
    });

    this.eventHandles = [];
    // this.isRegistered = false; // Reset registration flag

    Logger.debug('[WorkplaceEventRouter] ✅ Event listeners cleaned up');
  }

  /**
   * 🔧 DEBUG: Force clear no-handler cache
   */
  static debugClearNoHandlerCache(): void {
    Logger.debug('[WorkplaceEventRouter] 🔧 DEBUG: Clearing entire no-handler cache...');
    GlobalSocketService.clearNoHandlerCache();
    Logger.debug('[WorkplaceEventRouter] ✅ No-handler cache cleared');
  }

  /**
   * 🔧 DEBUG: Check no-handler events
   */
  static debugGetNoHandlerEvents(): string[] {
    const events = GlobalSocketService.getNoHandlerEventTypes();
    Logger.debug('[WorkplaceEventRouter] 🔧 DEBUG: Current no-handler events:', events);
    return events;
  }

  /**
   * Handle meeting join events from other users
   */
  private static handleMeetingJoin(event: any): void {
    try {
      const payload = event;
      if (!payload || !payload.user_pid) {
        console.warn('[WorkplaceEventRouter] Invalid meeting join event:', event);
        return;
      }

      Logger.debug('[WorkplaceEventRouter] 🎥 Meeting join:', {
        user_pid: payload.user_pid,
        meeting_pid: payload.meeting_pid,
        room_pid: payload.room_pid,
      });

      // Skip if this is our own event
      const currentUserPid = useWorkplaceStore.getState().user.pid;
      Logger.debug('[WorkplaceEventRouter] 🔍 User PID comparison:', {
        incoming_user_pid: payload.user_pid,
        current_user_pid: currentUserPid,
        is_same: payload.user_pid === currentUserPid,
        current_user_pid_empty: !currentUserPid,
      });

      // Only skip if we have a valid current user PID and it matches
      if (currentUserPid && payload.user_pid === currentUserPid) {
        Logger.debug('[WorkplaceEventRouter] Skipping own meeting join');
        return;
      }

      // If current user PID is empty, we're still initializing - process the event
      if (!currentUserPid) {
        Logger.debug(
          '[WorkplaceEventRouter] ⚠️ Current user PID not available yet, processing event from other user'
        );
      }

      // Update UI to show user joined meeting
      Logger.debug('[WorkplaceEventRouter] ✅ Meeting join from other user processed successfully');
    } catch (error) {
      console.error('[WorkplaceEventRouter] ❌ Error handling meeting join:', error);
    }
  }

  /**
   * Handle meeting leave events from other users
   */
  private static handleMeetingLeave(event: any): void {
    try {
      const payload = event;
      if (!payload || !payload.user_pid) {
        console.warn('[WorkplaceEventRouter] Invalid meeting leave event:', event);
        return;
      }

      Logger.debug('[WorkplaceEventRouter] 🎥 Meeting leave:', {
        user_pid: payload.user_pid,
        meeting_pid: payload.meeting_pid,
        room_pid: payload.room_pid,
      });

      // Skip if this is our own event
      const currentUserPid = useWorkplaceStore.getState().user.pid;
      Logger.debug('[WorkplaceEventRouter] 🔍 User PID comparison:', {
        incoming_user_pid: payload.user_pid,
        current_user_pid: currentUserPid,
        is_same: payload.user_pid === currentUserPid,
        current_user_pid_empty: !currentUserPid,
      });

      // Only skip if we have a valid current user PID and it matches
      if (currentUserPid && payload.user_pid === currentUserPid) {
        Logger.debug('[WorkplaceEventRouter] Skipping own meeting leave');
        return;
      }

      // If current user PID is empty, we're still initializing - process the event
      if (!currentUserPid) {
        Logger.debug(
          '[WorkplaceEventRouter] ⚠️ Current user PID not available yet, processing event from other user'
        );
      }

      // Update UI to show user left meeting
      Logger.debug(
        '[WorkplaceEventRouter] ✅ Meeting leave from other user processed successfully'
      );
    } catch (error) {
      console.error('[WorkplaceEventRouter] ❌ Error handling meeting leave:', error);
    }
  }

  /**
   * Handle user login events from other users
   */
  private static handleUserLogin(event: any): void {
    try {
      const payload = event;
      if (!payload || !payload.user_pid) {
        console.warn('[WorkplaceEventRouter] Invalid user login event:', event);
        return;
      }

      Logger.debug('[WorkplaceEventRouter] 👤 User login:', {
        user_pid: payload.user_pid,
        org_pid: payload.org_pid,
        floor_pid: payload.floor_pid,
        room_pid: payload.room_pid,
      });

      // Skip if this is our own event
      const currentUserPid = useWorkplaceStore.getState().user.pid;
      if (payload.user_pid === currentUserPid) {
        Logger.debug('[WorkplaceEventRouter] Skipping own user login');
        return;
      }

      // Update the workplace store with the new user location
      const workplaceStore = useWorkplaceStore.getState();
      if (workplaceStore.updateUserLocation && payload.room_pid && payload.floor_pid) {
        workplaceStore.updateUserLocation({
          user_pid: payload.user_pid,
          room_pid: payload.room_pid,
          floor_pid: payload.floor_pid,
        });
        Logger.debug('[WorkplaceEventRouter] ✅ Updated user location for login');
      }

      Logger.debug('[WorkplaceEventRouter] ✅ User login processed successfully');
    } catch (error) {
      console.error('[WorkplaceEventRouter] ❌ Error handling user login:', error);
    }
  }

  /**
   * Handle user logout events from other users
   */
  private static handleUserLogout(event: any): void {
    try {
      const payload = event;
      if (!payload || !payload.user_pid) {
        console.warn('[WorkplaceEventRouter] Invalid user logout event:', event);
        return;
      }

      Logger.debug('[WorkplaceEventRouter] 👤 User logout:', {
        user_pid: payload.user_pid,
        floor_pid: payload.floor_pid,
        room_pid: payload.room_pid,
      });

      // Skip if this is our own event
      const currentUserPid = useWorkplaceStore.getState().user.pid;
      if (payload.user_pid === currentUserPid) {
        Logger.debug('[WorkplaceEventRouter] Skipping own user logout');
        return;
      }

      // Remove user from workplace store
      const workplaceStore = useWorkplaceStore.getState();
      if (workplaceStore.updateUserLocation) {
        // Set user location to empty strings to remove them
        workplaceStore.updateUserLocation({
          user_pid: payload.user_pid,
          room_pid: '',
          floor_pid: '',
        });
        Logger.debug('[WorkplaceEventRouter] ✅ Removed user from location for logout');
      }

      Logger.debug('[WorkplaceEventRouter] ✅ User logout processed successfully');
    } catch (error) {
      console.error('[WorkplaceEventRouter] ❌ Error handling user logout:', error);
    }
  }
}
